// file path: src/Pages/WorkBot/stores/messages.slice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WorkbotMessage } from '../../../services/workbot/message.service';

export interface TemporaryMessage {
  id: string;
  conversationId: number;
  message: string;
  isBot: boolean;
  isTemporary: boolean;
  isThinking?: boolean;
  isRetry?: boolean;
  timestamp: number;
}

export interface MessagesState {
  messages: { [conversationId: number]: WorkbotMessage[] };
  temporaryMessages: { [conversationId: number]: TemporaryMessage[] };
  loading: { [conversationId: number]: boolean };
  retryMessages: { [conversationId: number]: { message: string; timestamp: number } };
}

const initialState: MessagesState = {
  messages: {},
  temporaryMessages: {},
  loading: {},
  retryMessages: {},
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setMessages: (
      state,
      action: PayloadAction<{ conversationId: number; messages: WorkbotMessage[] }>
    ) => {
      const { conversationId, messages } = action.payload;
      state.messages[conversationId] = messages;
    },
    
    addMessages: (
      state,
      action: PayloadAction<{ conversationId: number; messages: WorkbotMessage[] }>
    ) => {
      const { conversationId, messages } = action.payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      
      // For infinite scroll: older messages (from earlier pages) should be added at the beginning
      // The API returns messages in descending order (newest first), so we need to reverse them
      // to maintain chronological order (oldest first, newest last)
      const olderMessages = [...messages].reverse();
      state.messages[conversationId] = [...olderMessages, ...state.messages[conversationId]];
    },
    
    addMessage: (
      state,
      action: PayloadAction<{ conversationId: number; message: WorkbotMessage }>
    ) => {
      const { conversationId, message } = action.payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      state.messages[conversationId].push(message);
    },
    
    // Add temporary user message
    addTemporaryUserMessage: (
      state,
      action: PayloadAction<{ conversationId: number; message: string }>
    ) => {
      const { conversationId, message } = action.payload;
      if (!state.temporaryMessages[conversationId]) {
        state.temporaryMessages[conversationId] = [];
      }
      
      const tempMessage: TemporaryMessage = {
        id: `temp-${Date.now()}`,
        conversationId,
        message,
        isBot: false,
        isTemporary: true,
        timestamp: Date.now(),
      };
      
      state.temporaryMessages[conversationId].push(tempMessage);
    },
    
    // Add bot thinking state
    addBotThinking: (
      state,
      action: PayloadAction<{ conversationId: number }>
    ) => {
      const { conversationId } = action.payload;
      if (!state.temporaryMessages[conversationId]) {
        state.temporaryMessages[conversationId] = [];
      }
      
      const thinkingMessage: TemporaryMessage = {
        id: `thinking-${Date.now()}`,
        conversationId,
        message: 'WorkBot is thinking...',
        isBot: true,
        isTemporary: true,
        isThinking: true,
        timestamp: Date.now(),
      };
      
      state.temporaryMessages[conversationId].push(thinkingMessage);
    },
    
    // Remove only thinking state (for error cases)
    removeThinkingState: (
      state,
      action: PayloadAction<{ conversationId: number }>
    ) => {
      const { conversationId } = action.payload;
      if (state.temporaryMessages[conversationId]) {
        state.temporaryMessages[conversationId] = state.temporaryMessages[conversationId].filter(
          msg => !('isThinking' in msg && msg.isThinking)
        );
      }
    },
    
    // Remove temporary messages and add real messages
    replaceTemporaryWithReal: (
      state,
      action: PayloadAction<{
        conversationId: number;
        userMessage: WorkbotMessage;
        botMessage: WorkbotMessage;
      }>
    ) => {
      const { conversationId, userMessage, botMessage } = action.payload;
      
      // Remove temporary messages
      if (state.temporaryMessages[conversationId]) {
        state.temporaryMessages[conversationId] = [];
      }
      
      // Remove retry message
      delete state.retryMessages[conversationId];
      
      // Add real messages
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      state.messages[conversationId].push(userMessage, botMessage);
    },
    
    // Remove all temporary messages (for error cases)
    removeTemporaryMessages: (
      state,
      action: PayloadAction<{ conversationId: number }>
    ) => {
      const { conversationId } = action.payload;
      if (state.temporaryMessages[conversationId]) {
        state.temporaryMessages[conversationId] = [];
      }
    },
    
    // Add retry message
    addRetryMessage: (
      state,
      action: PayloadAction<{ conversationId: number; message: string }>
    ) => {
      const { conversationId, message } = action.payload;
      state.retryMessages[conversationId] = {
        message,
        timestamp: Date.now()
      };
    },
    
    // Remove retry message
    removeRetryMessage: (
      state,
      action: PayloadAction<{ conversationId: number }>
    ) => {
      const { conversationId } = action.payload;
      delete state.retryMessages[conversationId];
    },
    
    setLoading: (
      state,
      action: PayloadAction<{ conversationId: number; loading: boolean }>
    ) => {
      const { conversationId, loading } = action.payload;
      state.loading[conversationId] = loading;
    },
    
    clearMessages: (
      state,
      action: PayloadAction<{ conversationId: number }>
    ) => {
      const { conversationId } = action.payload;
      delete state.messages[conversationId];
      delete state.temporaryMessages[conversationId];
      delete state.loading[conversationId];
      delete state.retryMessages[conversationId];
    },
  },
});

export const {
  setMessages,
  addMessages,
  addMessage,
  addTemporaryUserMessage,
  addBotThinking,
  removeThinkingState,
  replaceTemporaryWithReal,
  removeTemporaryMessages,
  addRetryMessage,
  removeRetryMessage,
  setLoading,
  clearMessages,
} = messagesSlice.actions;

export default messagesSlice.reducer;
